import { gameEvents } from '../core/EventBus';
import { GameEvents } from '../config/events';

/**
 * Classify the reason an ability activation was blocked.
 *
 * Priority order (energyFailed is evaluated first because the energy gate
 * sits AFTER canActivate — so if energy fails, canActivate already passed):
 *   1. energyFailed=true → 'energy'
 *   2. cooldownRemaining > 0 → 'cooldown'
 *   3. (ability as any).unlocked === false → 'locked'
 *   4. residual (e.g. grounded / in-air check not exposed) → 'grounded'
 *
 * The `unlocked` field is not part of the core Ability interface but may be
 * present on concrete ability instances that support a locked state.
 */
export function classifyBlockReason(
  ability: Ability,
  energyFailed: boolean,
): 'cooldown' | 'energy' | 'locked' | 'grounded' {
  if (energyFailed) return 'energy';
  if (ability.cooldownRemaining > 0) return 'cooldown';
  // Optional `unlocked` field present on abilities that have a locked state.
  if ((ability as unknown as Record<string, unknown>)['unlocked'] === false) return 'locked';
  return 'grounded';
}

/**
 * Ability — the base interface for all character abilities.
 *
 * Abilities are composition-based: they're objects attached to an entity,
 * not subclasses. This means we can mix and match abilities freely,
 * and an ability can be shared across characters if needed.
 *
 * Lifecycle:
 *   activate()   -> the player presses the ability key
 *   update()     -> called each frame while active
 *   deactivate() -> the player releases, or duration expires, or interrupted
 *
 * Abilities emit events via the global EventBus so other systems
 * (UI, puzzles, audio) can react without direct coupling.
 */
export interface Ability {
  /** Unique identifier (e.g., 'auraSight', 'pneumaticAnchor') */
  readonly id: string;

  /** Display name for UI */
  readonly name: string;

  /** Whether this ability is currently active */
  isActive: boolean;

  /** Cooldown remaining in seconds (0 = ready) */
  cooldownRemaining: number;

  /** Total cooldown duration in seconds */
  readonly cooldownDuration: number;

  /** Activate the ability. Returns true if activation succeeded. */
  activate(): boolean;

  /** Called every frame while the ability is active. deltaTime is in seconds. */
  update(deltaTime: number): void;

  /** Deactivate the ability. */
  deactivate(): void;

  /** Check if the ability can be activated right now. */
  canActivate(): boolean;
}

/**
 * AbilityHolder — manages a set of abilities for a single character.
 *
 * Attached to Minna or Basil. Handles activation, cooldown ticking,
 * and event emission via the global EventBus.
 */
export class AbilityHolder {
  private abilities: Map<string, Ability> = new Map();
  private ownerId: string;
  /**
   * Optional energy gate: called before activation with the ability ID.
   * Returns true if activation should proceed, false to block it.
   * Set via setEnergyGate() — used by the energy system to drain/block abilities.
   */
  private energyGate: ((abilityId: string) => boolean) | null = null;

  constructor(ownerId: string) {
    this.ownerId = ownerId;
  }

  /**
   * Set an energy gate callback. When set, it is called before each ability
   * activation. If it returns false, activation is suppressed.
   * The gate is responsible for draining energy on successful activation.
   */
  setEnergyGate(gate: ((abilityId: string) => boolean) | null): void {
    this.energyGate = gate;
  }

  /**
   * Register an ability with this holder.
   */
  addAbility(ability: Ability): void {
    this.abilities.set(ability.id, ability);
  }

  /**
   * Remove an ability from this holder.
   */
  removeAbility(abilityId: string): void {
    const ability = this.abilities.get(abilityId);
    if (ability?.isActive) {
      ability.deactivate();
    }
    this.abilities.delete(abilityId);
  }

  /**
   * Try to activate an ability by ID.
   */
  activateAbility(abilityId: string): boolean {
    const ability = this.abilities.get(abilityId);
    if (!ability) return false;

    if (!ability.canActivate()) {
      gameEvents.emit(GameEvents.ABILITY_BLOCKED, {
        ownerId: this.ownerId,
        abilityId,
        reason: classifyBlockReason(ability, false),
      });
      return false;
    }

    // Energy gate check — if set, must return true to proceed
    if (this.energyGate && !this.energyGate(abilityId)) {
      gameEvents.emit(GameEvents.ABILITY_BLOCKED, {
        ownerId: this.ownerId,
        abilityId,
        reason: classifyBlockReason(ability, true),
      });
      return false;
    }

    const success = ability.activate();
    if (success) {
      gameEvents.emit(GameEvents.ABILITY_ACTIVATED, {
        ownerId: this.ownerId,
        abilityId: ability.id,
        abilityName: ability.name,
      });
    }
    return success;
  }

  /**
   * Deactivate an ability by ID.
   */
  deactivateAbility(abilityId: string): void {
    const ability = this.abilities.get(abilityId);
    if (ability?.isActive) {
      ability.deactivate();
      gameEvents.emit(GameEvents.ABILITY_DEACTIVATED, {
        ownerId: this.ownerId,
        abilityId: ability.id,
        abilityName: ability.name,
      });
    }
  }

  /**
   * Call every frame. Ticks cooldowns and updates active abilities.
   * @param deltaTime Time since last frame in seconds.
   */
  update(deltaTime: number): void {
    for (const ability of this.abilities.values()) {
      // Tick cooldowns
      if (ability.cooldownRemaining > 0) {
        ability.cooldownRemaining = Math.max(0, ability.cooldownRemaining - deltaTime);
      }

      // Update active abilities
      if (ability.isActive) {
        ability.update(deltaTime);
      }
    }
  }

  /**
   * Get an ability by ID.
   */
  getAbility(abilityId: string): Ability | undefined {
    return this.abilities.get(abilityId);
  }

  /**
   * Get all registered abilities.
   */
  getAllAbilities(): Ability[] {
    return Array.from(this.abilities.values());
  }
}
